import { SESSION_DATA_IDENTIFIER } from '../../../../api/utils/apollo/session/sessionToken.utils'
import localforage from '../../../../legacy/common/localforage'
import {
  fetchRetailerPlatformMeshClient,
  fetchRetailersClient,
} from '../../../../legacy/apollo/dashboardClient'
import { storeCurrentRedirectUrl } from '../../../../routes/logged-in/utils/loggedIn.utils'
import clientEnv from '../../../../utils/global/clientEnv'
import { trackLogout } from './trackLogout'

export enum LogoutReason {
  UserInitiated,
  TokenExpired,
  DisabledAccount,
}

/**
 * Logs the user out of the application.
 * This will:
 * - Empty all apollo stores for all clients
 * - Delete the session token
 * - Redirect to the login screen. If `tokenExpired` is true, it will also show a message on
 *   the login screen stating that fact.
 *
 * @param logoutReason
 * @param isInternal - Optional flag to indicate if the user is an internal Instacart user
 * @returns
 */
export const logout = async (
  logoutReason: LogoutReason = LogoutReason.UserInitiated,
  isInternal?: boolean
) => {
  const storeResetPromises = [fetchRetailersClient(), fetchRetailerPlatformMeshClient()].map(
    client => client.clearStore()
  )

  trackLogout({
    isExpired: logoutReason === LogoutReason.TokenExpired,
  })

  await Promise.all([
    ...storeResetPromises,
    localforage.removeItem(SESSION_DATA_IDENTIFIER),
    localforage.removeItem('hasRefreshToken'),
  ])

  // Blows away the app and reloads everything
  // Will ensure that nothing is cached, and ensure that the user
  // is sent back to the log-in page (after being redirected to auth service to clear session there)
  if (logoutReason != null) {
    const queryParam =
      logoutReason === LogoutReason.TokenExpired
        ? 'sessionExpired'
        : logoutReason === LogoutReason.DisabledAccount
        ? 'disabled'
        : undefined

    if (queryParam != null) {
      // If the user is getting logged out because of a token expiration
      // we try storing the current URL so we can redirect them later
      storeCurrentRedirectUrl()

      // Build query parameters
      const queryParams = new URLSearchParams()
      queryParams.set(queryParam, '1')

      // Add isInternal parameter only for internal users when being logged out for disabled account
      if (logoutReason === LogoutReason.DisabledAccount && isInternal === true) {
        queryParams.set('isInternal', '1')
      }

      return window.location.replace(
        `${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/logout?return_uri=${encodeURIComponent(
          `${window.location.origin}/public/log-in?${queryParams.toString()}`
        )}`
      )
    }
  }

  return window.location.replace(
    `${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/logout?return_uri=${encodeURIComponent(
      `${window.location.origin}/public/log-in`
    )}`
  )
}
